/**
 * Date Filtering Utilities
 *
 * Pure functions for filtering volunteer slots by date ranges.
 * Used in conjunction with date range filter buttons.
 */

/**
 * Date filter types for volunteer slot filtering
 *
 * - 'all': Show all slots (no date filtering)
 * - 'week': Show slots in the next 7 days (including today)
 * - 'month': Show slots in the current month (from today onwards)
 * - 'future': Show all future slots (including today)
 * - 'custom': Show slots in a custom date range (requires customDateFrom and customDateTo in localStorage)
 */
export type DateFilter = 'all' | 'week' | 'month' | 'future' | 'custom'

/**
 * Filter slots by date range based on selected filter type
 *
 * @param dateStr - Date string in yyyy-mm-dd format
 * @param filter - Type of date filter to apply
 * @returns True if date passes the filter, false otherwise
 *
 * @example
 * // Show all dates
 * filterByDateRange('2025-10-15', 'all') // Returns: true
 *
 * // Show only next 7 days
 * filterByDateRange('2025-10-15', 'week') // Returns: true (if within 7 days from today)
 *
 * // Show only current month
 * filterByDateRange('2025-10-15', 'month') // Returns: true (if in October 2025 and today is Oct 2025)
 *
 * // Show only future dates
 * filterByDateRange('2025-10-15', 'future') // Returns: true (if today is before or on 2025-10-15)
 *
 * // Show custom date range
 * filterByDateRange('2025-10-15', 'custom') // Returns: true (if date is within customDateFrom and customDateTo)
 */
export const filterByDateRange = (dateStr: string, filter: DateFilter): boolean => {
  // 'all' filter shows everything
  if (filter === 'all') return true

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const slotDate = new Date(dateStr)
  slotDate.setHours(0, 0, 0, 0)

  // 'custom' filter: show dates in custom range from localStorage
  if (filter === 'custom') {
    const customFrom = localStorage.getItem('customDateFrom')
    const customTo = localStorage.getItem('customDateTo')

    if (!customFrom || !customTo) {
      // If custom dates not set, fall back to showing all
      return true
    }

    const fromDate = new Date(customFrom)
    fromDate.setHours(0, 0, 0, 0)

    const toDate = new Date(customTo)
    toDate.setHours(0, 0, 0, 0)

    return slotDate >= fromDate && slotDate <= toDate
  }

  // 'future' filter: show all dates from today onwards
  if (filter === 'future') {
    return slotDate >= today
  }

  // 'week' filter: show dates in the next 7 days (including today)
  if (filter === 'week') {
    const weekFromNow = new Date(today)
    weekFromNow.setDate(weekFromNow.getDate() + 7)
    return slotDate >= today && slotDate <= weekFromNow
  }

  // 'month' filter: show dates in the current month (from today onwards)
  if (filter === 'month') {
    return (
      slotDate.getMonth() === today.getMonth() &&
      slotDate.getFullYear() === today.getFullYear() &&
      slotDate >= today
    )
  }

  return true
}
